'use client';
import React, { useState } from 'react';

interface Plugin {
  id: string;
  name: string;
  description: string;
  author: string;
  category: string;
  verified: boolean;
}

const plugins: Plugin[] = [
  // Development Tools
  { id: 'vscode', name: 'Visual Studio Code', description: 'Sync VS Code settings, extensions, and keybindings', author: 'ConfigSync Team', category: 'development', verified: true },
  { id: 'cursor', name: 'Cursor', description: 'Cursor editor settings, extensions, and keybindings', author: 'ConfigSync Team', category: 'development', verified: true },
  { id: 'vim', name: 'Vim/Neovim', description: 'Complete Vim and Neovim configuration sync', author: 'ConfigSync Team', category: 'development', verified: true },
  { id: 'git', name: 'Git Configuration', description: 'Global git config, aliases, and ignore patterns', author: 'ConfigSync Team', category: 'development', verified: true },
  { id: 'jetbrains', name: 'JetBrains IDEs', description: 'IntelliJ IDEA, WebStorm, PyCharm, and IdeaVim settings', author: 'ConfigSync Team', category: 'development', verified: true },
  { id: 'sublime', name: 'Sublime Text', description: 'Preferences, packages, and snippets', author: 'community', category: 'development', verified: false },
  { id: 'xcode', name: 'Xcode', description: 'Xcode themes, keybindings, and code snippets', author: 'community', category: 'development', verified: false },
  { id: 'emacs', name: 'Emacs', description: 'Emacs init files, packages, and custom themes', author: 'community', category: 'development', verified: false },
  { id: 'helix', name: 'Helix', description: 'Helix editor config, themes, and languages', author: 'community', category: 'development', verified: false },
  { id: 'zed', name: 'Zed', description: 'Zed editor settings, keybindings, and themes', author: 'community', category: 'development', verified: false },
  { id: 'npm', name: 'npm', description: '.npmrc auth tokens and registry configuration', author: 'ConfigSync Team', category: 'development', verified: true },
  { id: 'pip', name: 'pip', description: 'pip.conf and global Python package settings', author: 'community', category: 'development', verified: false },
  { id: 'cargo', name: 'Cargo', description: 'Rust Cargo config and installed binaries', author: 'community', category: 'development', verified: false },
  { id: 'go', name: 'Go', description: 'Go environment variables and tool configuration', author: 'community', category: 'development', verified: false },
  { id: 'composer', name: 'Composer', description: 'PHP Composer global config and auth', author: 'community', category: 'development', verified: false },
  { id: 'gradle', name: 'Gradle', description: 'Gradle properties and init scripts', author: 'community', category: 'development', verified: false },
  { id: 'maven', name: 'Maven', description: 'Maven settings.xml and toolchains', author: 'community', category: 'development', verified: false },
  { id: 'editorconfig', name: 'EditorConfig', description: 'Global .editorconfig formatting rules', author: 'community', category: 'development', verified: false },
  { id: 'prettier', name: 'Prettier', description: 'Global Prettier configuration', author: 'community', category: 'development', verified: false },
  { id: 'eslint', name: 'ESLint', description: 'Global ESLint config and rules', author: 'community', category: 'development', verified: false },

  // Terminal & Shell
  { id: 'zsh', name: 'Zsh + Oh My Zsh', description: 'Complete Zsh configuration with Oh My Zsh themes and plugins', author: 'ConfigSync Team', category: 'terminal', verified: true },
  { id: 'bash', name: 'Bash', description: 'Bashrc, bash_profile, and aliases', author: 'ConfigSync Team', category: 'terminal', verified: true },
  { id: 'fish', name: 'Fish Shell', description: 'Fish config, functions, and completions', author: 'ConfigSync Team', category: 'terminal', verified: true },
  { id: 'tmux', name: 'Tmux', description: 'Tmux configuration and plugins', author: 'ConfigSync Team', category: 'terminal', verified: true },
  { id: 'starship', name: 'Starship', description: 'Cross-shell prompt configuration', author: 'ConfigSync Team', category: 'terminal', verified: true },
  { id: 'alacritty', name: 'Alacritty', description: 'Fast GPU-accelerated terminal configuration', author: 'community', category: 'terminal', verified: false },
  { id: 'iterm2', name: 'iTerm2', description: 'macOS terminal preferences and profiles', author: 'community', category: 'terminal', verified: false },
  { id: 'kitty', name: 'Kitty', description: 'Kitty terminal emulator configuration', author: 'community', category: 'terminal', verified: false },
  { id: 'wezterm', name: 'WezTerm', description: 'WezTerm terminal config and Lua scripts', author: 'community', category: 'terminal', verified: false },
  { id: 'hyper', name: 'Hyper', description: 'Hyper terminal preferences and plugins', author: 'community', category: 'terminal', verified: false },
  { id: 'nushell', name: 'Nushell', description: 'Nushell config, env, and custom commands', author: 'community', category: 'terminal', verified: false },
  { id: 'powershell', name: 'PowerShell', description: 'PowerShell profile, modules, and scripts', author: 'community', category: 'terminal', verified: false },

  // Cloud & DevOps
  { id: 'aws', name: 'AWS CLI', description: 'AWS config, credentials, and SSO profiles', author: 'ConfigSync Team', category: 'cloud', verified: true },
  { id: 'docker', name: 'Docker', description: 'Docker daemon config and registry auth', author: 'ConfigSync Team', category: 'cloud', verified: true },
  { id: 'kubernetes', name: 'Kubernetes', description: 'Kubeconfig contexts and cluster credentials', author: 'ConfigSync Team', category: 'cloud', verified: true },
  { id: 'wrangler', name: 'Cloudflare Wrangler', description: 'Wrangler CLI auth and project config', author: 'ConfigSync Team', category: 'cloud', verified: true },
  { id: 'gcloud', name: 'Google Cloud CLI', description: 'gcloud config, credentials, and project settings', author: 'ConfigSync Team', category: 'cloud', verified: true },
  { id: 'azure', name: 'Azure CLI', description: 'Azure CLI config and auth tokens', author: 'community', category: 'cloud', verified: false },
  { id: 'terraform', name: 'Terraform', description: 'Terraform CLI config and provider credentials', author: 'community', category: 'cloud', verified: false },
  { id: 'helm', name: 'Helm', description: 'Helm repositories and plugin configuration', author: 'community', category: 'cloud', verified: false },
  { id: 'vercel', name: 'Vercel', description: 'Vercel CLI auth and project settings', author: 'community', category: 'cloud', verified: false },
  { id: 'netlify', name: 'Netlify', description: 'Netlify CLI auth and site configuration', author: 'community', category: 'cloud', verified: false },
  { id: 'fly', name: 'Fly.io', description: 'Fly CLI auth tokens and app config', author: 'community', category: 'cloud', verified: false },
  { id: 'railway', name: 'Railway', description: 'Railway CLI auth and project settings', author: 'community', category: 'cloud', verified: false },
  { id: 'heroku', name: 'Heroku', description: 'Heroku CLI auth and app configuration', author: 'community', category: 'cloud', verified: false },
  { id: 'ansible', name: 'Ansible', description: 'Ansible config, vault passwords, and inventory', author: 'community', category: 'cloud', verified: false },

  // AI Tools
  { id: 'claude-code', name: 'Claude Code', description: 'Claude Code settings, credentials, and keybindings', author: 'ConfigSync Team', category: 'ai', verified: true },
  { id: 'claude-desktop', name: 'Claude Desktop', description: 'Claude Desktop config and MCP server definitions', author: 'ConfigSync Team', category: 'ai', verified: true },
  { id: 'github-copilot', name: 'GitHub Copilot', description: 'Copilot settings and auth across editors', author: 'community', category: 'ai', verified: false },
  { id: 'continue', name: 'Continue', description: 'Continue.dev config, models, and custom commands', author: 'community', category: 'ai', verified: false },
  { id: 'openai', name: 'OpenAI CLI', description: 'OpenAI API keys and CLI configuration', author: 'community', category: 'ai', verified: false },
  { id: 'ollama', name: 'Ollama', description: 'Ollama model config and Modelfiles', author: 'community', category: 'ai', verified: false },

  // Browsers
  { id: 'arc', name: 'Arc Browser', description: 'Arc spaces, profiles, and boost configurations', author: 'ConfigSync Team', category: 'browser', verified: true },
  { id: 'chrome', name: 'Google Chrome', description: 'Chrome bookmarks, extensions list, and preferences', author: 'community', category: 'browser', verified: false },
  { id: 'firefox', name: 'Firefox', description: 'Firefox profiles, about:config, and extensions', author: 'community', category: 'browser', verified: false },
  { id: 'brave', name: 'Brave', description: 'Brave browser settings and bookmarks', author: 'community', category: 'browser', verified: false },

  // Security
  { id: 'ssh', name: 'SSH', description: 'SSH keys, config, and known_hosts with encryption', author: 'ConfigSync Team', category: 'security', verified: true },
  { id: 'gpg', name: 'GPG', description: 'GPG config, agent settings, and key metadata', author: 'ConfigSync Team', category: 'security', verified: true },
  { id: 'age', name: 'Age Encryption', description: 'Age keys and recipients file', author: 'community', category: 'security', verified: false },
  { id: '1password-cli', name: '1Password CLI', description: '1Password CLI config and account settings', author: 'community', category: 'security', verified: false },

  // Productivity
  { id: 'raycast', name: 'Raycast', description: 'Extensions, quicklinks, and script commands', author: 'ConfigSync Team', category: 'productivity', verified: true },
  { id: 'obsidian', name: 'Obsidian', description: 'Vault settings, plugins, and themes', author: 'community', category: 'productivity', verified: false },
  { id: 'alfred', name: 'Alfred', description: 'Alfred preferences and workflows (macOS)', author: 'community', category: 'productivity', verified: false },
  { id: 'karabiner', name: 'Karabiner-Elements', description: 'Keyboard customization rules (macOS)', author: 'ConfigSync Team', category: 'productivity', verified: true },
  { id: 'hammerspoon', name: 'Hammerspoon', description: 'Lua automation scripts for macOS', author: 'community', category: 'productivity', verified: false },
  { id: 'espanso', name: 'Espanso', description: 'Cross-platform text expander configuration', author: 'community', category: 'productivity', verified: false },
  { id: 'bettertouchtool', name: 'BetterTouchTool', description: 'Trackpad, keyboard, and window snap presets', author: 'community', category: 'productivity', verified: false },
  { id: 'rectangle', name: 'Rectangle', description: 'Window management shortcuts (macOS)', author: 'community', category: 'productivity', verified: false },

  // System & Package Managers
  { id: 'macos', name: 'macOS Preferences', description: 'System preferences, defaults, and dock layout', author: 'ConfigSync Team', category: 'system', verified: true },
  { id: 'homebrew', name: 'Homebrew', description: 'Brewfile with packages, casks, and taps', author: 'ConfigSync Team', category: 'system', verified: true },
  { id: 'apt', name: 'APT Packages', description: 'Debian/Ubuntu package lists and sources', author: 'ConfigSync Team', category: 'system', verified: true },
  { id: 'snap', name: 'Snap', description: 'Snap package list and connections', author: 'community', category: 'system', verified: false },
  { id: 'flatpak', name: 'Flatpak', description: 'Flatpak apps and remote repositories', author: 'community', category: 'system', verified: false },
  { id: 'scoop', name: 'Scoop', description: 'Scoop buckets and package lists (Windows)', author: 'community', category: 'system', verified: false },
  { id: 'winget', name: 'Winget', description: 'Windows Package Manager export and install', author: 'community', category: 'system', verified: false },
  { id: 'nix', name: 'Nix', description: 'Nix config, channels, and home-manager', author: 'community', category: 'system', verified: false },
  { id: 'bat', name: 'bat', description: 'bat (cat alternative) config and themes', author: 'community', category: 'system', verified: false },
  { id: 'ripgrep', name: 'ripgrep', description: 'ripgrep global config and ignore rules', author: 'community', category: 'system', verified: false },
  { id: 'fd', name: 'fd', description: 'fd-find ignore rules and configuration', author: 'community', category: 'system', verified: false },
  { id: 'fzf', name: 'fzf', description: 'Fuzzy finder config, keybindings, and themes', author: 'community', category: 'system', verified: false },
  { id: 'htop', name: 'htop', description: 'htop display and color settings', author: 'community', category: 'system', verified: false },

  // Databases
  { id: 'pgcli', name: 'pgcli', description: 'PostgreSQL CLI config and named queries', author: 'community', category: 'database', verified: false },
  { id: 'mycli', name: 'mycli', description: 'MySQL CLI config and favorites', author: 'community', category: 'database', verified: false },
  { id: 'dbeaver', name: 'DBeaver', description: 'DBeaver connections, drivers, and preferences', author: 'community', category: 'database', verified: false },
  { id: 'tableplus', name: 'TablePlus', description: 'TablePlus connections and preferences', author: 'community', category: 'database', verified: false },
  { id: 'redis-cli', name: 'Redis CLI', description: 'Redis CLI config and connection aliases', author: 'community', category: 'database', verified: false },
];

const categories = [
  { id: 'all', name: 'All Plugins', icon: '🔌' },
  { id: 'development', name: 'Development', icon: '💻' },
  { id: 'terminal', name: 'Terminal & Shell', icon: '🖥️' },
  { id: 'cloud', name: 'Cloud & DevOps', icon: '☁️' },
  { id: 'ai', name: 'AI Tools', icon: '🤖' },
  { id: 'browser', name: 'Browsers', icon: '🌐' },
  { id: 'security', name: 'Security', icon: '🔐' },
  { id: 'productivity', name: 'Productivity', icon: '🚀' },
  { id: 'system', name: 'System', icon: '⚙️' },
  { id: 'database', name: 'Databases', icon: '🗄️' },
];

export default function PluginsPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleInstallCopy = (pluginId: string) => {
    navigator.clipboard.writeText(`devsync plugin install ${pluginId}`);
    setCopiedId(pluginId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredPlugins = plugins.filter(plugin => {
    const matchesCategory = selectedCategory === 'all' || plugin.category === selectedCategory;
    const matchesSearch = plugin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         plugin.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Plugin Gallery</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Extend ConfigSync with plugins for your favorite tools and applications
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search plugins..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg whitespace-nowrap transition ${
                    selectedCategory === category.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="mr-2">{category.icon}</span>
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Plugin Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPlugins.map((plugin) => (
            <div key={plugin.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    {plugin.name}
                    {plugin.verified && (
                      <span className="text-blue-500" title="Verified by ConfigSync Team">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-500">by {plugin.author}</p>
                </div>
              </div>

              <p className="text-gray-600 mb-4">{plugin.description}</p>

              <div className="flex items-center justify-end">
                <button
                  onClick={() => handleInstallCopy(plugin.id)}
                  className={`px-4 py-2 rounded-lg transition text-sm font-medium ${
                    copiedId === plugin.id
                      ? 'bg-green-600 text-white'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  {copiedId === plugin.id ? 'Copied!' : 'Copy Install CMD'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Create Plugin CTA */}
        <div className="mt-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Create Your Own Plugin</h2>
          <p className="text-xl mb-6 opacity-90">
            Extend ConfigSync with custom plugins for your workflows
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/docs/plugin-development" className="bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition">
              Plugin Development Guide
            </a>
            <a href="https://github.com/InventiveHQ/configsync-plugins" className="bg-primary-800 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-900 transition">
              View on GitHub
            </a>
          </div>
        </div>

        {/* Installation Instructions */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold mb-4">How to Install Plugins</h2>
          <div className="space-y-4">
            <div>
              <p className="text-gray-600 mb-2">Install a plugin using the CLI:</p>
              <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono">
                <p className="text-green-400"># Install a specific plugin</p>
                <p>configsync plugin install vscode</p>
                <p className="text-green-400 mt-3"># List installed plugins</p>
                <p>configsync plugin list</p>
                <p className="text-green-400 mt-3"># Update all plugins</p>
                <p>configsync plugin update</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}